import { Button } from "@clockwise/design-system";
import { Edit, FileCopyOutlined } from "@clockwise/design-system/icons";
import { CalendarClockFilled } from "@clockwise/icons";
import { AvailabilityRestrictionEnum } from "@clockwise/schema/v2";
import { animated, useSpring } from "@react-spring/web";
import classNames from "classnames";
import React, { useState } from "react";
import { formatDuration } from "../../util/duration.util";
import { AttendeeAvatar } from "../AttendeeAvatar";
import { AttendeeAvatarStack } from "../AttendeeAvatarStack";
import { AVAILABILITY_TEXT_SHORT } from "../scheduling-links/constants";
import { useConfetti } from "./useConfetti";

export interface LinkMember {
  calendarId: string;
  userProfile: {
    givenName: string | null;
    familyName: string | null;
    externalImageUrl: string | null;
  };
}

interface IProps {
  linkMembers: LinkMember[];
  name: string;
  active: boolean;
  durations: number[];
  defaultDuration: number;
  availabilityType: AvailabilityRestrictionEnum;
  onOpen: () => Promise<void>;
  onCopyTimes: () => Promise<void>;
  onCopyLink: () => Promise<void>;
  onEditLink?: () => Promise<void>;
}

export const SchedulingLinkCard = ({
  active,
  availabilityType,
  durations,
  defaultDuration,
  linkMembers,
  name,
  onOpen,
  onCopyLink,
  onCopyTimes,
  onEditLink,
}: IProps) => {
  const formattedDuration =
    durations.length > 1 ? `${durations.length} duration options` : formatDuration(defaultDuration);
  const [copied, setCopied] = useState(false);

  const confettiCanvasRef = React.useRef<HTMLCanvasElement>(null);
  const { triggerConfetti, resetConfetti } = useConfetti();

  const { textScale } = useSpring({
    textScale: copied ? 0 : 1,
    immediate: !copied, // Prevents a second animation when copied is returned to false.
    config: { mass: 1, tension: 170, friction: 18, precision: 0.02 },
  });
  const { copiedMsgOpacity } = useSpring({
    copiedMsgOpacity: copied ? 1 : 0,
    config: { mass: 1, tension: 200, friction: 23 },
  });
  const scaleInterpolateRange = [0, 0.25, 0.35, 0.45, 0.5, 0.55, 0.65, 0.75, 1];
  const scaleInterpolateOutput = [1.04, 1.06, 1.07, 1.08, 1.1, 1.09, 1.08, 1.06, 1];

  const handleCopyLink = React.useCallback(async () => {
    await onCopyLink();
    confettiCanvasRef.current && triggerConfetti(confettiCanvasRef.current);

    setCopied(true);
    setTimeout(() => {
      setCopied(false);
      resetConfetti();
    }, 2100);
  }, [onCopyLink, resetConfetti, triggerConfetti]);

  return (
    <div
      className={classNames(
        "cw-relative cw-max-w-[425px] cw-px-4 cw-py-2 ",
        "cw-border cw-border-solid cw-border-default cw-shadow-md cw-rounded-lg",
        {
          "cw-bg-default": active,
          "cw-bg-default-disabled": !active,
        },
      )}
    >
      <div className="cw-flex cw-flex-col cw-gap-1 cw-z-0">
        <div className="cw-flex cw-justify-stretch cw-items-center">
          <h2
            className={classNames(
              "cw-heading-sm cw-mt-1 cw-gap-1",
              "cw-flex cw-justify-stretch cw-items-start cw-flex-shrink cw-mr-auto",
              "cw-whitespace-nowrap cw-overflow-hidden cw-text-ellipsis",
              {
                "hover:cw-text-brand-hover focus:cw-text-brand-hover active:cw-text-brand-pressed cw-cursor-pointer": active,
                "cw-text-brand-disabled": !active,
              },
            )}
            cw-id="links-card-meeting-title"
            onClick={() => {
              active && void onOpen();
            }}
          >
            <span className="cw-flex-shrink cw-overflow-hidden cw-text-ellipsis cw-whitespace-nowrap">
              {name}
            </span>
          </h2>
          <div className="cw-justify-self-end -cw-mr-2">
            <Button
              rounded
              size="mini"
              sentiment="neutral"
              variant="text"
              startIcon={Edit}
              aria-label="Edit link"
              onClick={onEditLink}
            />
          </div>
        </div>
        {linkMembers.length > 1 && (
          // Display in an AvatarStack of UserAvatars
          <AttendeeAvatarStack maxShown={8} size="medium" overlap>
            {linkMembers.map((linkMember) => (
              <AttendeeAvatar
                key={linkMember.calendarId}
                profile={linkMember.userProfile}
                size="medium"
              />
            ))}
          </AttendeeAvatarStack>
        )}
        <div
          className={classNames("cw-body-base", {
            "cw-text-subtle": active,
            "cw-text-subtle-disabled": !active,
          })}
        >
          {formattedDuration} • {AVAILABILITY_TEXT_SHORT[availabilityType]}
        </div>
        {active && (
          <div className="-cw-ml-[10px] cw-flex cw-flex-row cw-justify-around cw-flex-nowrap cw-overflow-hidden cw-gap-2">
            <Button
              onClick={handleCopyLink}
              variant="text"
              size="small"
              sentiment="positive"
              startIcon={FileCopyOutlined}
              cw-id="links-card-copy-link-button"
            >
              Copy link
            </Button>
            <Button
              onClick={onCopyTimes}
              variant="text"
              size="small"
              startIcon={CalendarClockFilled}
              sentiment="neutral"
              cw-id="links-card-copy-times-button"
            >
              Share times
            </Button>
          </div>
        )}
      </div>
      <animated.div
        className="cw-absolute cw-inset-0 cw-flex cw-items-center cw-justify-center cw-bg-brand-emphasis cw-rounded-lg cw-z-10 cw-cursor-default"
        style={{ opacity: copiedMsgOpacity, visibility: copied ? "visible" : "hidden" }}
      >
        <canvas ref={confettiCanvasRef} className="cw-absolute cw-w-full cw-h-full" />
        <animated.div
          className="cw-body-base cw-text-onEmphasis"
          style={{
            transform: textScale
              .to({
                range: scaleInterpolateRange,
                output: scaleInterpolateOutput,
              })
              .to((x) => `scale(${x})`),
          }}
        >
          Link copied to your clipboard 🙌
        </animated.div>
      </animated.div>
    </div>
  );
};
